import React, { useCallback, useContext, useEffect, useRef } from "react"
import { useHistory } from "react-router"

import styled from "styled-components"

import { TranslatorContext, useLocalization, useMarkdown } from "@izettle/lingo-client"
import { IllustrationEcomIntegrationSvg, IllustrationEmptyStateThreeSvg, IllustrationGenericErrorSvg, spacing } from "@izettle/otto"
import { Button, Text, Illustration } from "@izettle/react"

import { PageWrapper } from "@/components/layout-helpers"
import { docifySupportContactUrl } from "@/constants"
import { redirectToBackOffice } from "@/helpers"
import { ErrorContent } from "@/models"
import { closeWebview } from "@/util"

const StyledPageWrapper = styled(PageWrapper)`
  align-items: center;

  &.modal {
    margin: ${spacing.large} ${spacing.none};
    min-height: unset;
    width: 100%;
  }
`

const ImageContainer = styled.div`
  max-width: 100%;
  height: auto;
  margin-bottom: ${spacing.small};
`;

const TextWrapper = styled.div`
  text-align: center;
  margin-bottom: ${spacing.small};
`

const ErrorTitle = styled(Text.Huge)`
  margin-top: ${spacing.none};
`

const ErrorText = styled(Text.RegularSecondary)`
  margin-top: ${spacing.none};
  margin-bottom: ${spacing.none};
`

type ErrorProps = {
  cancelUrl?: string
  errorType?: string
  isModal?: boolean
}

export const Error: React.FC<ErrorProps> = ({
  cancelUrl,
  isModal = false,
  errorType = "default",
}) => {
  const { translator } = useContext(TranslatorContext)
  const { text } = useLocalization()
  const { mdElement } = useMarkdown()
  const history = useHistory()
  const retryBtn = useRef<HTMLButtonElement>(null)

  const errorContent: ErrorContent = {
    default: {
      title: text("GENERIC_ERROR_TITLE"),
      generalErrorText: text("TRY_AGAIN_ERROR_TEXT"),
      additionalErrorText: mdElement("CONTACT_SUPPORT_ERROR_TEXT", {
        replacements: [docifySupportContactUrl.get(translator.locale)],
        textKey: true,
      }),
      illustration: IllustrationGenericErrorSvg,
      buttonText: text("RETRY"),
    },
    loaDataLost: {
      title: text("LOA_DATA_LOST_ERROR_TTITLE"),
      generalErrorText: text("LOA_DATA_LOST_ERROR_TEXT"),
      illustration: IllustrationEmptyStateThreeSvg,
      buttonText: text("RETRY"),
    },
    invalidFileFormat: {
      title: text("INVALID_FILE_FORMAT_ERROR_TITLE"),
      generalErrorText: text("INVALID_FILE_FORMAT_ERROR_TEXT"),
      illustration: IllustrationEcomIntegrationSvg,
      buttonText: text("RETRY_UPLOAD"),
    },
  }

  const pageWrapperClassName = useCallback(() => {
    return isModal ? "modal" : ""
  }, [])

  const cancelAndRedirect = useCallback(() => {
    if (!cancelUrl) {
      closeWebview()
      redirectToBackOffice()
      return
    }

    history.replace(cancelUrl)
  }, [cancelUrl])

  useEffect(() => {
    retryBtn?.current?.focus()
  }, [retryBtn?.current])

  // Always show the default error page if the errorType URL parameter has an unexpected value
  if (!(errorType in errorContent)) {
    errorType = "default"
  }

  const additionalErrorTextIsString =
    typeof errorContent[errorType].additionalErrorText === "string"

  return (
    <StyledPageWrapper className={pageWrapperClassName()}>
      <ImageContainer>
        <Illustration illustration={errorContent[errorType].illustration} alt="" width="100%" height="150px" aria-hidden  />
      </ImageContainer>
      <TextWrapper id="errorMessage">
        <ErrorTitle as="h2">{errorContent[errorType].title}</ErrorTitle>
        <ErrorText>{errorContent[errorType].generalErrorText}</ErrorText>
        {errorContent[errorType].additionalErrorText &&
          (additionalErrorTextIsString ? (
            <ErrorText>{errorContent[errorType].additionalErrorText}</ErrorText>
          ) : (
            <ErrorText as="div">{errorContent[errorType].additionalErrorText}</ErrorText>
          ))}
      </TextWrapper>
      <Button ref={retryBtn} aria-describedby="errorMessage" onClick={cancelAndRedirect}>
        {errorContent[errorType].buttonText}
      </Button>
    </StyledPageWrapper>
  )
}
